import React, { useMemo, useState } from 'react';
import { KeenIcon } from '@/components';
import {
  AvatarUpload,
  InputForm,
  IntroImage,
  AgentAdvancedSettings
} from './builder-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCopy, faLink, faShareNodes, faSliders } from '@fortawesome/free-solid-svg-icons';
import { UpdateAgentMutation } from '@/hooks/useAgentBuilder';
import BtnSubmitAuth from '@/components/ui/btnSubmitAuth';
import { useAuth } from '@/hooks/useAuth';
import { Modal } from 'antd';
import { FormattedMessage } from 'react-intl';
import { faFacebook } from '@fortawesome/free-brands-svg-icons';

const AgentBuilder = ({
  onFormChange,
  initialData,
  settings,
  onSettingsChange,
  introImageSettings,
  onIntroImageChange,
  onSave,
  hasChanges,
  isOpen,
  onToggle
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [formData, setFormData] = useState({});
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [shareMode, setShareMode] = useState('embed');
  const [copied, setCopied] = useState(false);

  const { userData } = useAuth();
  const embedLink = useMemo(() => {
    const uID = userData?.uID || '';
    return `https://sdk.chatme.vn/${uID}`;
  }, [userData?.uID]);

  const updateAgentMutation = UpdateAgentMutation({
    onSuccess: () => {
      onSave?.();
    }
  });

  const handleIntroImageChange = (data) => {
    onIntroImageChange?.({ enableIntroImage: data.enableIntroImage });
  };

  const handleFormChange = (data) => {
    setFormData(data);
    onFormChange?.(data);
  };

  const handleSettingsChange = (newSettings) => {
    // Gửi thay đổi settings lên preview (không ảnh hưởng đến form save)
    onSettingsChange?.(newSettings);
  };

  const handleSave = () => {
    const dataToSave = {
      ...formData,
      intro_image_state: introImageSettings.enableIntroImage ? 1 : 0,
    };

    updateAgentMutation.mutate(dataToSave);
  };

  const handleOpenShare = (mode = 'embed') => {
    setShareMode(mode);
    setIsShareOpen(true);
  };

  const handleCloseShare = () => {
    setIsShareOpen(false);
    setCopied(false);
  };

  const copyToClipboard = (textToCopy) => {
    if (!textToCopy) return;
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const zaloLink = 'https://id.zalo.me/account/login?continue=https%3A%2F%2Foauth.zaloapp.com%2Fv4%2Foa%2Fpermission%3Fapp_id%3D1357978611269843648%26redirect_uri%3Dhttps%253A%252F%252Fsocial.chatme.vn%252Fwebhook%252Fzalo';

  return (
    <div>
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between py-2 px-4 hover:bg-gray-200 border border-gray border-opacity-50 
          ${isOpen ? 'bg-gray-200' : 'bg-white'} rounded-md transition-all duration-300 ease-in-out`}
      >
        <div className='text-sm font-semibold'>
          <KeenIcon icon="setting-4" className="mr-2" />
          <FormattedMessage id="AGENTS.BUILDER.AGENT_BUILDER" />
        </div>
        <span className="text-gray-500 transition-transform duration-300">
          {isOpen ? <KeenIcon icon="down" /> : <KeenIcon icon="right" />}
        </span>
      </button>

      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'opacity-100 mt-3' : 'max-h-0 opacity-0'
          }`}
      >
        <div className="space-y-2 p-4 border rounded-lg">
          {showAdvanced ? (
            <AgentAdvancedSettings
              onBack={() => setShowAdvanced(false)}
              {...settings}
              onChange={handleSettingsChange}
            />
          ) : (
            <>
              <AvatarUpload />
              <InputForm onFormChange={handleFormChange} initialData={initialData} />
              <IntroImage onChange={handleIntroImageChange} />
              <div className="flex items-center gap-2">
                <div className='flex gap-2'>
                  <button onClick={() => handleOpenShare('embed')} className='border rounded-md px-2 py-1.75 flex items-center text-[#50B553] hover:text-[#4AA642] focus:text-[#50CD54] active:text-[#325928]'>
                    <FontAwesomeIcon icon={faShareNodes} className='size-4'/>
                  </button>
                  {/* <button onClick={() => handleOpenShare('embed')} className='border rounded-md px-2 py-1.75 flex items-center text-[#50B553] hover:text-[#4AA642] focus:text-[#50CD54] active:text-[#325928]'>
                    <FontAwesomeIcon icon={faFacebook} className='size-4'/>
                  </button> */}
                  <button onClick={() => handleOpenShare('other')} className='border rounded-md px-2 py-1.75 flex items-center text-[#50B553] hover:text-[#4AA642] focus:text-[#50CD54] active:text-[#325928]'>
                    <svg height="16" viewBox="0 0 460.1 436.6" width="16" xmlns="http://www.w3.org/2000/svg" className="text-current">
                      <path fill="currentColor" d="M139.5 436.2c-27.3 0-54.7.9-82-.1-32.3-1.3-57-28.4-57-60.7 0-104.3.2-208.6 0-313C.5 26.7 28.4 1.8 60.5.9c33.6-.9 67.3-.2 101-.2.6 0 1.4-.3 1.7.7-.2 1.8-2 2-3.1 2.7-19.8 11.6-37.9 25.5-53.1 42.7-25.1 28.4-42.5 61-48.4 98.9-10.4 66.9 10.5 123.7 57.8 171.1 8.4 8.5 9.5 15.1 2.8 26.5-8.1 13.7-20.4 23-33.5 31.5-1.4.8-2.8 1.8-4.2 2.7-2.1 1.8-.8 2.7 1 3.5.4.9.9 1.7 1.5 2.5 11.5 10.2 22.4 21.1 33.7 31.5 5.3 4.9 10.6 10 15.7 15.1 2.1 1.9 5.6 2.5 6.1 6.1z" />
                      <path fill="currentColor" d="M139.5 436.2c-.5-3.5-4-4.1-6.1-6.2-5.1-5.2-10.4-10.2-15.7-15.1-11.3-10.4-22.2-21.3-33.7-31.5-.6-.8-1.1-1.6-1.5-2.5 25.5 5 50.4 1.6 74.9-5.9 8.3-2.5 16.6-5 25-7.1 5.7-1.5 11.7-1.2 17.2.8 63.4 21.8 126 19.8 187.6-7.2 25.1-11.1 48-26.7 67.4-46.2 1-1 1.7-2.5 3.5-2.6.9 1.4.4 2.9.4 4.4v58.5c.2 33.4-26.6 60.6-60 60.9h-.5c-36 .2-72 0-108 0H145.5c-2-.2-4-.3-6-.3z" />
                      <path fill="currentColor" d="M155.1 212.4c15.1 0 29.3-.1 43.4 0 7.9.1 12.2 3.4 13 9.7.9 7.9-3.7 13.2-12.3 13.3-16.2.2-32.3.1-48.5.1-4.7 0-9.3.2-14-.1-5.8-.3-11.5-1.5-14.3-7.5s-.8-11.4 3-16.3c15.4-19.6 30.9-39.3 46.4-58.9.9-1.2 1.8-2.4 2.7-3.5-1-1.7-2.4-.9-3.6-1-10.8-.1-21.7 0-32.5-.1-2.5 0-5-.3-7.4-.8-5.7-1.3-9.2-7-7.9-12.6.9-3.8 3.9-6.9 7.7-7.8 2.4-.6 4.9-.9 7.4-.9 17.8-.1 35.7-.1 53.5 0 3.2-.1 6.3.3 9.4 1.1 6.8 2.3 9.7 8.6 7 15.2-2.4 5.7-6.2 10.6-10 15.5-13.1 16.7-26.2 33.3-39.3 49.8-1.1 1.3-2.1 2.6-3.7 4.8z" />
                      <path fill="currentColor" d="M271.1 159.8c2.4-3.1 4.9-6 9-6.8 7.9-1.6 15.3 3.5 15.4 11.5.3 20 .2 40 0 60 0 5.2-3.4 9.8-8.3 11.3-5 1.9-10.7.4-14-3.9-1.7-2.1-2.4-2.5-4.8-.6-9.1 7.4-19.4 8.7-30.5 5.1-17.8-5.8-25.1-19.7-27.1-36.6-2.1-18.3 4-33.9 20.4-43.5 13.6-8.1 27.4-7.4 39.9 3.5zm-35.4 36.5c.2 4.4 1.6 8.6 4.2 12.1 5.4 7.2 15.7 8.7 23 3.3 1.2-.9 2.3-2 3.3-3.3 5.6-7.6 5.6-20.1 0-27.7-2.8-3.9-7.2-6.2-11.9-6.3-11-.7-18.7 7.8-18.6 21.9zM340.4 196.9c-.8-25.7 16.1-44.9 40.1-45.6 25.5-.8 44.1 16.3 44.9 41.3.8 25.3-14.7 43.2-38.6 45.6-26.1 2.6-46.8-16.3-46.4-41.3zm25.1-2.4c-.2 5 1.3 9.9 4.3 14 5.5 7.2 15.8 8.6 23 3 1.1-.8 2-1.8 2.9-2.8 5.8-7.6 5.8-20.4.1-28-2.8-3.8-7.2-6.2-11.9-6.3-10.8-.6-18.4 7.6-18.4 20.1zM331.6 177c0 15.5.1 31 0 46.5.1 7.1-5.5 13-12.6 13.2-1.2 0-2.5-.1-3.7-.4-5-1.3-8.8-6.6-8.8-12.9v-79.5c0-4.7-.1-9.3 0-14 .1-7.7 5-12.7 12.4-12.7 7.6-.1 12.7 4.9 12.7 12.9.1 15.6 0 31.3 0 46.9z" />
                      <path fill="currentColor" d="M235.7 196.3c-.1-14.1 7.6-22.6 18.5-22 4.7.2 9.1 2.5 11.9 6.4 5.6 7.5 5.6 20.1 0 27.7-5.4 7.2-15.7 8.7-23 3.3-1.2-.9-2.3-2-3.3-3.3-2.5-3.5-3.9-7.7-4.1-12.1zM365.5 194.5c0-12.4 7.6-20.7 18.4-20.1 4.7.1 9.1 2.5 11.9 6.3 5.7 7.6 5.7 20.5-.1 28-5.6 7.1-16 8.3-23.1 2.7-1.1-.8-2-1.8-2.8-2.9-3-4.1-4.4-9-4.3-14z" />
                    </svg>
                  </button>
                </div>
                <button
                  onClick={() => setShowAdvanced(true)}
                  className="w-full border rounded-md px-3 py-1 flex items-center gap-1 bg-white justify-center"
                >
                  <FontAwesomeIcon icon={faSliders} /> <FormattedMessage id="AGENTS.HOME.ADVANCED_SETTINGS" />
                </button>
              </div>
              <BtnSubmitAuth
                onClick={handleSave}
                disabled={!hasChanges}
                className={`w-full py-2 mt-4 font-bold flex items-center justify-center gap-2 ${hasChanges ? 'bg-[#00B14F]' : 'bg-gray-400 hover:bg-gray-400'} rounded-md`}
              >
                <KeenIcon icon="save-2" />
                {hasChanges ? <FormattedMessage id="AGENTS.HOME.SAVE_CHANGES" /> : <FormattedMessage id="AGENTS.HOME.SAVED" />}
              </BtnSubmitAuth>

              <Modal
                open={isShareOpen}
                onCancel={handleCloseShare}
                footer={null}
                centered
                title={
                  shareMode === 'embed'
                    ? <div className="flex items-center gap-2"><FontAwesomeIcon icon={faLink} /><FormattedMessage id="AGENTS.BUILDER.EMBED_LINK_MODAL_TITLE" /></div>
                    : <div className="flex items-center gap-2"><FontAwesomeIcon icon={faLink} />Zalo OA</div>
                }
              >
                {shareMode === 'embed' ? (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600"><FormattedMessage id="AGENTS.BUILDER.EMBED_LINK_MODAL_DESCRIPTION" /></p>
                    <div className="relative">
                      <input
                        readOnly
                        value={embedLink}
                        className="w-full border rounded px-2 py-1 pr-9 text-sm"
                      />
                      <button
                        onClick={() => copyToClipboard(embedLink)}
                        title={copied ? <FormattedMessage id="AGENTS.BUILDER.COPIED_BUTTON" /> : <FormattedMessage id="AGENTS.BUILDER.COPY_BUTTON" />}
                        className="absolute right-2 top-1/2 -translate-y-1/2 transform transition-all duration-300 ease-in-out"
                      >
                        <FontAwesomeIcon
                          icon={copied ? faCircleCheck : faCopy}
                          className={`${copied ? 'text-[#50B553]' : 'text-gray-500 hover:text-[#4AA642]'}`}
                        />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600"><FormattedMessage id="AGENTS.BUILDER.EMBED_LINK_MODAL_DESCRIPTION_ZALO" /></p>
                    <div className="relative">
                      <input
                        readOnly
                        value={zaloLink}
                        className="w-full border rounded px-2 py-1 pr-9 text-sm"
                      />
                      <button
                        onClick={() => copyToClipboard(zaloLink)}
                        title={copied ? <FormattedMessage id="AGENTS.BUILDER.COPIED_BUTTON" /> : <FormattedMessage id="AGENTS.BUILDER.COPY_BUTTON" />}
                        className="absolute right-2 top-1/2 -translate-y-1/2 transform transition-all duration-300 ease-in-out"
                      >
                        <FontAwesomeIcon
                          icon={copied ? faCircleCheck : faCopy}
                          className={`${copied ? 'text-[#50B553]' : 'text-gray-500 hover:text-[#4AA642]'}`}
                        />
                      </button>
                    </div>
                  </div>
                )}
              </Modal>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const MemoAgentBuilder = React.memo(AgentBuilder);

export { MemoAgentBuilder as AgentBuilder };
